import ApiService from './api-service';
import {
  PENDING_INVOICES,
  COLLECTING_INVOICES,
  MISSING_EMAIL,
  MISSING_NAME,
  READY_FOR_COLLECTION,
  COLLECTED_INVOICES,
  WAITING_CONTRACT,
} from '../util/filters';
import get from 'lodash/get';
import RateError from '../util/RateError';
import { ALL_COLLECTIONS_INVOICES } from '@/util/constants';

const CollectorService = {
  async getCollectionsDebtor({ contactId, shortCode }) {
    const body = {
      query: `query ($ContactID: String!) {
        organisation {
          contact(ContactID: $ContactID) {
           collectionDetails {
              notes
            }
            collectionsDebtor {
              debtorType
              address {
                address
                city
                zipCode
                state
                country
              }
            }
          }
        }
      }`,
      variables: {
        ContactID: contactId,
      },
    };

    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);
    return get(organisation, 'contact.collectionsDebtor');
  },
  async getFilterCounts(shortCode) {
    const body = {
      query: `query ($filter: InvoiceFilter) {
        organisation {
          collectingInvoices: invoices(
            filter: {
              collectorStatus: "COLLECTING"
            }
          ) {
            totalCount
          }
          pendingInvoices: invoices(
            filter: {
              collectorStatus: "PENDING"
            }
          ) {
            totalCount
          }
          missingNames: invoices(
            missingNames: true,
            filter: $filter
          ) {
            totalCount
          }
          missingEmailAddress: invoices(
            missingEmailAddress: true,
            filter: $filter
          ) {
            totalCount
          }
          readyForCollector: invoices(
            filter: $filter
          ) {
            totalCount
          }
          collected: invoices(
            filter: {
              collectorStatus: "COLLECTED",
              paid: true,
            }
          ) {
            totalCount
          }
          waitingContract: invoices(
            filter: {
              collectorStatus: "WAITING_CONTRACT"
            }
          ) {
            totalCount
          }
        }
      }`,
      variables: {
        filter: ALL_COLLECTIONS_INVOICES,
      },
    };

    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);

    return {
      [PENDING_INVOICES]: organisation.pendingInvoices.totalCount,
      [COLLECTING_INVOICES]: organisation.collectingInvoices.totalCount,
      [MISSING_EMAIL]: organisation.missingEmailAddress.totalCount,
      [MISSING_NAME]: organisation.missingNames.totalCount,
      [READY_FOR_COLLECTION]: organisation.readyForCollector.totalCount,
      [COLLECTED_INVOICES]: organisation.collected.totalCount,
      [WAITING_CONTRACT]: organisation.waitingContract.totalCount,
    };
  },
  async getStats(shortCode) {
    const body = {
      query: `query {
          organisation {
            collector {
              collecting {
                total
                count
              }
              collected {
                total
                count
                collectionIncrease
              }
              collectable {
                total
                count
              }
            }
          }
        }`,
    };
    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);

    return organisation.collector;
  },
  async getCollectorRestrictions(shortCode) {
    const body = {
      query: `query {
          organisation {
            collector {
              restrictions {
                above
              }
            }
          }
        }`,
    };
    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);

    return organisation.collector.restrictions;
  },
  async getSuggestedInvoices({ shortCode, contactId, selectedInvoices }) {
    const body = {
      query: `query ($collectable: InvoiceFilter, $contactId: String) {
          organisation {
            invoices(filter: $collectable, ContactID: $contactId) {
              nodes {
                _id
                InvoiceNumber
                DueDate
                AmountDue
                CurrencyCode
                InvoiceID
                Total
                contact {
                  _id
                  Name
                  ContactID
                }
              }
            }
          }
        }`,
      variables: {
        contactId,
        collectable: ALL_COLLECTIONS_INVOICES,
      },
    };

    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);

    const additionalInvoices = organisation.invoices.nodes.filter(({ _id: aId }) => {
      return !selectedInvoices.find(({ _id: sId }) => {
        return sId === aId;
      });
    });

    return additionalInvoices;
  },
  async collectorPriceCalculator({
    shortCode,
    invoiceIds,
    countryCode,
    debtorType,
    debtorAddress,
  }) {
    const body = {
      query: `query ($invoiceIds: [String!], $countryCode: String!, $debtorType: String!, $debtorAddress: CollectorRatesDebtorAddressInput!) {
        organisation {
          collectorRates(invoiceIds: $invoiceIds, countryCode: $countryCode, debtorType: $debtorType, debtorAddress: $debtorAddress) {
            rate
          }
        }
      }`,
      variables: {
        invoiceIds,
        countryCode,
        debtorType,
        debtorAddress,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    if (data.organisation.collectorRates.length === 0)
      throw new RateError('No valid invoices selected for escalation');

    const [rate] = data.organisation.collectorRates;
    return rate.rate;
  },
  async beginCollector({ shortCode, invoiceIds, countryCode, notes, debtorType, debtorAddress }) {
    const body = {
      query: `mutation($invoiceIds: [String!], $countryCode: String!, $notes: String!, $debtorType: String!, $debtorAddress: BeginCollectorDebtorAddressInput!) {
        beginCollector(invoiceIds: $invoiceIds, countryCode: $countryCode, notes: $notes, debtorType: $debtorType, debtorAddress: $debtorAddress) {
          url
          caseId
        }
      }`,
      variables: {
        notes,
        invoiceIds,
        countryCode,
        debtorType,
        debtorAddress,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data?.beginCollector;
  },

  async setCollectionsDebtorData({ shortCode, contactId, debtorType, address }) {
    const body = {
      query: `mutation($contactId: String!, $debtorType: String!, $address: InputCollectionsDebtorAddressType) {
        setCollectionsDebtorData(contactId: $contactId, debtorType: $debtorType, address: $address)
      }`,
      variables: {
        contactId,
        debtorType,
        address,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data?.setCollectionsDebtorData;
  },
};

export default CollectorService;
